/**
 * Export control for list pages: CSV, Excel or PDF of the CURRENT filter
 * selection.
 *
 * The download is produced server-side. Lists here are paginated, so a
 * browser-side export could only ever contain the page in view - it would
 * hand the operator a fraction of what their filters selected while
 * looking like the whole list.
 *
 * Usage:
 *   <ExportMenu
 *     resource="bookings"          // /api/bookings/bookings/export/
 *     params={buildFilterParams()} // the SAME params the list request uses
 *     onError={setError}
 *   />
 */

import React, { useState } from 'react';
import {
  Button,
  CircularProgress,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import TableViewIcon from '@mui/icons-material/TableView';
import DescriptionIcon from '@mui/icons-material/Description';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import apiClient from '../api/client';

const FORMATS = [
  { key: 'csv', label: 'CSV', icon: <DescriptionIcon fontSize="small" /> },
  { key: 'xlsx', label: 'Excel', icon: <TableViewIcon fontSize="small" /> },
  { key: 'pdf', label: 'PDF', icon: <PictureAsPdfIcon fontSize="small" /> },
];

const ExportMenu = ({ resource, params = {}, onError, size = 'medium' }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [exporting, setExporting] = useState(false);

  const handleExport = async (exportFormat) => {
    setAnchorEl(null);
    try {
      setExporting(true);
      if (onError) onError('');
      const { blob, filename } = await apiClient.downloadListExport(
        resource,
        params,
        exportFormat
      );
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      // The server explains refusals (too many rows, unsupported format,
      // portal caller); show that rather than a generic failure.
      if (onError) onError(err.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        size={size}
        startIcon={exporting ? <CircularProgress size={18} /> : <FileDownloadIcon />}
        disabled={exporting}
        onClick={(event) => setAnchorEl(event.currentTarget)}
      >
        {exporting ? 'Exporting…' : 'Export'}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        {FORMATS.map((format) => (
          <MenuItem key={format.key} onClick={() => handleExport(format.key)}>
            <ListItemIcon>{format.icon}</ListItemIcon>
            <ListItemText>{format.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default ExportMenu;
